import store from '@/store';

export const EMPRESA_NUEVA_MUTUAL = 103;
export const EMPRESA_BSA = 20;
export const EMPRESA_PILAIT = 21;

const GRUPO_SIN_OVERRIDE = 1;

/**
 * Perfiles de logo contextual.
 * Un override futuro por sección = nueva entrada en `byPath` (orden: primera coincidencia gana).
 */
const LOGO_PROFILES = {
   nuevaMutual: {
      default: EMPRESA_PILAIT,
      byPath: [
         { match: '/automotor', id: EMPRESA_BSA },
         { match: '/creditos', id: EMPRESA_NUEVA_MUTUAL },
      ],
   },
   generico: {
      default: EMPRESA_PILAIT,
      byPath: [{ match: '/creditos', id: EMPRESA_NUEVA_MUTUAL }],
   },
};

const SECCION_AGRO = 1;

const resolveFromProfile = (profile, path) => {
   const p = String(path || '');
   for (const rule of profile.byPath) {
      if (p.includes(rule.match)) {
         return rule.id;
      }
   }
   return profile.default;
};

const resolveByPathOrUndefined = (profile, path) => {
   const p = String(path || '');
   for (const rule of profile.byPath) {
      if (p.includes(rule.match)) {
         return rule.id;
      }
   }
   return undefined;
};

/**
 * @param {Array<{ id_seccion?: number|string }>|null|undefined} sections
 * @returns {boolean|undefined} true/false si hay dato; undefined si aún no cargaron
 */
export const resolveTieneSeccion1 = (sections) => {
   if (!Array.isArray(sections)) {
      return undefined;
   }
   return sections.some((s) => Number(s.id_seccion) === SECCION_AGRO);
};

/**
 * Default Pilait del perfil genérico, gateado por sección 1 (Agro).
 * - con sección 1 → null (logo de la empresa del usuario)
 * - sin sección 1 → Pilait
 * - sections aún no cargadas → null (no forzar Pilait)
 */
const resolveGenericoDefault = (tieneSeccion1) => {
   if (tieneSeccion1 === true) {
      return null;
   }
   if (tieneSeccion1 === false) {
      return EMPRESA_PILAIT;
   }
   return null;
};

const iconCache = new Map();

/**
 * @param {number|string|null|undefined} idEmpresaUsuario
 * @param {string} path
 * @param {number|string|null|undefined} idGrupoUsuario
 * @param {boolean|undefined} tieneSeccion1
 * @returns {number|null} id_empresa del logo a mostrar, o null si corresponde el logo del usuario
 */
export const resolveLogoEmpresaId = (
   idEmpresaUsuario,
   path = '',
   idGrupoUsuario,
   tieneSeccion1,
) => {
   const idEmpresa = Number(idEmpresaUsuario);
   const idGrupo = Number(idGrupoUsuario);

   if (!idEmpresa) {
      return null;
   }

   if (idEmpresa === EMPRESA_NUEVA_MUTUAL) {
      return resolveFromProfile(LOGO_PROFILES.nuevaMutual, path);
   }

   if (idGrupo === GRUPO_SIN_OVERRIDE || idEmpresa === EMPRESA_BSA) {
      return null;
   }

   const byPath = resolveByPathOrUndefined(LOGO_PROFILES.generico, path);
   if (byPath !== undefined) {
      return byPath;
   }

   return resolveGenericoDefault(tieneSeccion1);
};

const arrayBufferToBase64 = (buffer, mimeType) => {
   const bytes = new Uint8Array(buffer);
   let binary = '';
   for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
   }
   return `data:${mimeType};base64,${btoa(binary)}`;
};

/**
 * Obtiene icono de empresa como data-URL, con cache en memoria.
 */
export const getCompanyIconBase64 = async (
   idEmpresa,
   { esPng = false } = {},
) => {
   const key = `${Number(idEmpresa)}:${esPng ? 'png' : 'svg'}`;
   if (iconCache.has(key)) {
      return iconCache.get(key);
   }
   try {
      const { data, mimeType } = await store.dispatch('main/getCompanyIcon', {
         id_empresa: Number(idEmpresa),
         esPng,
      });
      const byteLength = data?.byteLength ?? data?.length ?? 0;
      if (!byteLength) {
         return null;
      }
      const base64 = arrayBufferToBase64(data, mimeType);
      iconCache.set(key, base64);
      return base64;
   } catch (error) {
      console.warn(
         `No se pudo obtener logo empresa ${idEmpresa}:`,
         error,
      );
      return null;
   }
};

/**
 * Precarga y aplica el logo contextual del header para una ruta dada.
 * Si no hay override, aplica el logo de la empresa del usuario.
 * @param {number|string|null|undefined} idEmpresaUsuario
 * @param {string} path
 * @param {number|string|null|undefined} idGrupoUsuario
 * @param {boolean|undefined} tieneSeccion1
 * @returns {Promise<boolean>} true si se aplicó un logo
 */
export const syncContextualLogoForRoute = async (
   idEmpresaUsuario,
   path,
   idGrupoUsuario,
   tieneSeccion1,
) => {
   const logoEmpresaId = resolveLogoEmpresaId(
      idEmpresaUsuario,
      path,
      idGrupoUsuario,
      tieneSeccion1,
   );
   const targetId =
      logoEmpresaId != null ? logoEmpresaId : Number(idEmpresaUsuario);
   if (!targetId) {
      return false;
   }
   const base64 = await getCompanyIconBase64(targetId, { esPng: false });
   if (!base64) {
      return false;
   }
   store.commit('main/setCompanyIconBase64', base64);
   return true;
};

/** Solo para tests / logout si hiciera falta limpiar. */
export const clearCompanyIconCache = () => {
   iconCache.clear();
};
